import { LoaderIcon } from '@/app/features/icons/Loader'
import { FaIcon } from '@/lib/fa'
import { faClose } from '@fortawesome/pro-light-svg-icons'
import React, { useEffect } from 'react'
import ModalLayout from '../../shared/ModalLayout'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import useContractService from '@/modules/drivers/services/ContractService'
import TemplatesTable from './TemplatesListTable'
import { TableProvider, useTable } from '@/modules/drivers/context/TableContext'
import { useConfirm } from '@/modules/shared/context/ConfirmContext'
import { toast } from '@/lib/toasts/api'

const TemplatesInner = ({
  templates,
  setSelectedContractIdFromTemplate,
  setSelectedTemplateId,
  onDelete,
  loading,
  t,
}) => {
  const { isSelectedAny } = useTable()

  return (
    <>
      {!loading && templates.length === 0 ? (
        <div className="tw-flex tw-flex-1 tw-flex-col tw-items-center tw-justify-center tw-gap-4">
          <p className="tw-text-sm tw-text-content-base">
            Brak stworzonych szablonów
          </p>
        </div>
      ) : (
        <TemplatesTable
          templates={templates}
          setSelectedContractIdFromTemplate={setSelectedContractIdFromTemplate}
          setSelectedTemplateId={setSelectedTemplateId}
          onDelete={onDelete}
        />
      )}
      <footer className="tw-flex tw-justify-end">
        <button
          disabled={loading || !isSelectedAny}
          type="submit"
          className="tw-m-4 tw-cursor-pointer tw-rounded-lg tw-bg-primary tw-p-3 tw-text-sm tw-font-medium tw-text-white hover:tw-opacity-80 disabled:tw-cursor-not-allowed disabled:tw-opacity-40"
        >
          {t('operation.choose')}
        </button>
      </footer>
    </>
  )
}

const AddContractByTemplate = ({
  close,
  companyId,
  setSelectedTemplateId,
  setIsOpenContractWithDataFromTemplate,
  setSelectedContractIdFromTemplate,
}) => {
  const [loading, setLoading] = React.useState(false)
  const { getContractTemplates, deletecontractTemplate } = useContractService()
  const [templates, setTemplates] = React.useState([])
  const { t } = useLocalize()
  const confirm = useConfirm()

  const handleDelete = async (template) => {
    const ok = await confirm({
      title: t('operation.delete'),
      message: t('contractTemplatesPopup.template-delete-confirm').replace(
        '{0}',
        template.templateName,
      ),
      confirmText: t('operation.delete'),
      cancelText: t('operation.cancel'),
    })
    if (!ok) return

    setLoading(true)
    try {
      await deletecontractTemplate({
        companyId,
        templateId: template.templateId,
      })
      setTemplates((prev) =>
        prev.filter((x) => x.templateId !== template.templateId),
      )
      toast.success({
        text: t('contractTemplatesPopup.template-removed-details').replace(
          '{0}',
          template.templateName,
        ),
      })
    } catch (error) {
      console.error('Error deleting template:', error)
      toast.error({ text: t('contractTemplatesPopup.template-delete-failed') })
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    setIsOpenContractWithDataFromTemplate(true)
    close()
  }

  useEffect(() => {
    const fetchTemplates = async () => {
      setLoading(true)
      try {
        const data = await getContractTemplates({
          companyId: companyId,
        })
        setTemplates(data)
      } catch (error) {
        console.error('Error creating template:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchTemplates()
  }, [])
  return (
    <ModalLayout customZIndex={250}>
      <form
        onSubmit={handleSubmit}
        onClick={(e) => {
          e.stopPropagation()
        }}
        noValidate
        className="tw-relative tw-flex tw-h-fit tw-max-h-[90vh] tw-w-full tw-max-w-[760px] tw-flex-col tw-rounded-lg tw-bg-white tw-shadow-2xl"
      >
        {loading && (
          <div className="tw-absolute tw-inset-0 tw-z-[200] tw-grid tw-place-items-center tw-rounded-lg tw-bg-black/40">
            <LoaderIcon className="tw-animate-spin" />
          </div>
        )}
        <div className="tw-flex tw-w-full tw-items-center tw-justify-between tw-p-4">
          <p className="tw-m-0 tw-text-xl tw-font-semibold tw-text-content-base">
            {t('contractTemplatesPopup.title')}
          </p>

          <FaIcon
            icon={faClose}
            className="tw-mr-4 tw-h-5 tw-w-5 tw-cursor-pointer tw-text-content-base hover:tw-text-primary"
            onClick={() => close()}
          />
        </div>
        <TableProvider bulk={false}>
          <TemplatesInner
            templates={templates}
            setSelectedContractIdFromTemplate={
              setSelectedContractIdFromTemplate
            }
            setSelectedTemplateId={setSelectedTemplateId}
            onDelete={handleDelete}
            loading={loading}
            t={t}
          />
        </TableProvider>
      </form>
    </ModalLayout>
  )
}

export default AddContractByTemplate
